import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import GenericModal from '../../components/agv-generic-modal';
import Row from '../../components/agv-generic-table/row';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import { isConsignmentPackagingEnabled } from '../../utils/consignment.util';
import moment from 'moment';
import AddressPhotos from './panels/address-photos';
import AddressDetails from './panels/address-details';
import AddressConsignedPackagings from './panels/address-consigned-packagings';

export default function AddressModal({ isOpen, setIsOpen, address }) {
  const [focusedTabIndex, setFocusedTabIndex] = useState(0);
  const features = useSelector((state) => state.features);
  const showConsignmentTab = isConsignmentPackagingEnabled(features);

  const tabs = useMemo(() => {
    const items = [
      {
        key: 'photos',
        label: 'Photos',
        render: (isFocused) => (
          <AddressPhotos
            addressId={address?.id}
            addressKey={address?.address_key}
            clientId={address?.client_id}
            isFocused={isFocused}
          />
        ),
      },
      {
        key: 'details',
        label: 'Détails complets',
        render: (isFocused) => (
          <AddressDetails addressId={address?.id} isFocused={isFocused} />
        ),
      },
    ];

    if (showConsignmentTab) {
      items.push({
        key: 'consignment',
        label: 'Palettes consignées',
        render: (isFocused) => (
          <AddressConsignedPackagings
            addressId={address?.id}
            isFocused={isFocused}
          />
        ),
      });
    }

    return items;
  }, [address, showConsignmentTab]);

  /**
   * Format the full address
   * @param {object} address - The address element
   * @returns {string} - The formatted address
   */
  const formatFullAddress = (address) => {
    const parts = [];
    if (address?.address_number) parts.push(address.address_number);
    if (address?.street) parts.push(address.street);
    if (address?.zipcode) parts.push(address.zipcode);
    if (address?.city) parts.push(address.city);
    if (address?.country) parts.push(address.country);
    return parts.join(', ') || '-';
  };

  /**
   * Format the title with address_key and name
   * @param {object} address - The address element
   * @returns {string} - The formatted title
   */
  const formatTitle = (address) => {
    const addressKey = address?.address_key || 'Non renseigné';
    const name = address?.name || '';

    if (!name || addressKey === name) {
      return addressKey;
    }

    return `${addressKey} - ${name}`;
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={`Détails du destinataire`}
      size={MODAL_SIZE_STYLE[MODAL_SIZE.XXL]}
    >
      <div className="tw-flex tw-flex-col tw-border-solid tw-border-0 tw-border-b tw-border-background tw-pb-2">
        <Row
          title={formatTitle(address)}
          subtitle={formatFullAddress(address)}
          iconColor={'tw-text-checklist-common'}
          icon={faLocationDot}
          fields={[
            {
              name: 'Dernière livraison',
              value: address?.last_delivery_date
                ? moment(new Date(address.last_delivery_date)).format(
                    'DD MMM YYYY'
                  )
                : '-',
            },
            {
              name: 'Nombre de livraisons',
              value: address?.total_deliveries || '0',
            },
            {
              name: 'Nombre de photos',
              value: address?.photos_count || '0',
            },
          ]}
          status={STATUS_ENUM.NEUTRAL}
        />
      </div>

      <TabGroup
        onChange={setFocusedTabIndex}
        defaultIndex={0}
        style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <TabList
          className={
            'tw-w-full tw-rounded-md tw-py-2 tw-px-2 tw-bg-slategray-default tw-border tw-border-solid tw-space-x-4 tw-border-[#dfe3e6]'
          }
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              className="tw-rounded-md tw-border-0 tw-text-sm tw-py-1.5 tw-px-3 tw-bg-transparent data-[selected]:tw-bg-main data-[selected]:tw-text-white"
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels
          style={{
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {tabs.map((tab, index) => (
            <TabPanel key={tab.key}>
              {tab.render(focusedTabIndex === index)}
            </TabPanel>
          ))}
        </TabPanels>
      </TabGroup>
    </GenericModal>
  );
}
